import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnChanges,
  OnDestroy,
} from '@angular/core'
import {DatePipe} from '@angular/common'

import {BehaviorSubject, Subject, Subscription, of} from 'rxjs'
import {
  combineLatest,
  debounceTime,
  switchMap,
  tap,
  flatMap,
  catchError,
} from 'rxjs/operators'

import {LoggerService} from '@app/core/services/logger.service'

import {Chart} from 'chart.js'
import {SearchFilter} from '../search-filter'
import {DateFilter} from '@app/core/services/date-filter/date-filter.interface'
import {
  TransactionChartDataService,
  XAxisType,
  StackType,
  TransactionData,
} from './transaction-chart-data.service'
import {NotificationService} from '@app/core/services/notification.service'
import {ContextService} from '@app/core/services/context.service'

interface ChartSelector {
  name: string
  fn: any
}

@Component({
  selector: 'app-transaction-chart',
  templateUrl: './transaction-chart.component.html',
  providers: [DatePipe, TransactionChartDataService],
  standalone: false,
})
export class TransactionChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  filter: SearchFilter
  filterSubject: BehaviorSubject<SearchFilter> = new BehaviorSubject(null)

  @Input()
  range: DateFilter
  rangeSubject: BehaviorSubject<DateFilter> = new BehaviorSubject(null)

  loading$: Subject<boolean> = new BehaviorSubject(false)
  totalsTypes: string[] = []

  currencies: ChartSelector[] = []
  chartTypes: ChartSelector[] = []
  xAxisTypes: ChartSelector[] = [
    {
      name: 'Hour',
      fn: () => {
        this.xAxisTypeSubject.next(XAxisType.HOUR)
      },
    },
    {
      name: 'Day',
      fn: () => {
        this.xAxisTypeSubject.next(XAxisType.DAY)
      },
    },
    {
      name: 'Week',
      fn: () => {
        this.xAxisTypeSubject.next(XAxisType.WEEK)
      },
    },
    {
      name: 'Month',
      fn: () => {
        this.xAxisTypeSubject.next(XAxisType.MONTH)
      },
    },
    {
      name: 'Terminal',
      fn: () => {
        this.xAxisTypeSubject.next(XAxisType.TERMINAL)
      },
    },
    {
      name: 'Item Ref',
      fn: () => {
        this.xAxisTypeSubject.next(XAxisType.ITEM_REF)
      },
    },
  ]

  stackTypes: ChartSelector[] = [
    {
      name: 'Card Holder Device',
      fn: () => {
        this.stackTypeSubject.next(StackType.CHD)
      },
    },
    {
      name: 'Disposition',
      fn: () => {
        this.stackTypeSubject.next(StackType.DISPOSITION)
      },
    },
    {
      name: 'Host',
      fn: () => {
        this.stackTypeSubject.next(StackType.HOST)
      },
    },
    {
      name: 'State',
      fn: () => {
        this.stackTypeSubject.next(StackType.STATE)
      },
    },
    {
      name: 'Type',
      fn: () => {
        this.stackTypeSubject.next(StackType.TYPE)
      },
    },
  ]

  activeChartType: ChartSelector
  activeXAxisType: ChartSelector
  activeStackType: ChartSelector
  activeCurrency: ChartSelector

  /* The chart.js object, build when new activeChartConfig is set */
  chart: Chart
  txnData: TransactionData

  private subscriptions: Subscription[] = []
  private activeChartSubject: Subject<Chart.ChartConfiguration> =
    new BehaviorSubject(null)

  private xAxisTypeSubject: Subject<XAxisType> = new Subject()
  private stackTypeSubject: Subject<StackType> = new Subject()
  private currencySubject: Subject<string> = new Subject()

  constructor(
    private logger: LoggerService,
    private dataService: TransactionChartDataService,
    private notify: NotificationService,
    private contextSvc: ContextService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.contextSvc.activeDomain$
        .pipe(
          combineLatest(this.filterSubject, this.rangeSubject),
          debounceTime(20),
          switchMap((value: [string, SearchFilter, DateFilter]) =>
            of(value).pipe(
              flatMap((value: [string, SearchFilter, DateFilter]) => {
                return this.dataService.getCurrencies(
                  value[0],
                  value[1],
                  value[2],
                )
              }),
              catchError((err) => {
                this.logger.error(err)
                this.notify.error(err.message)
                return of<string[]>([])
              }),
            ),
          ),
          tap((value) => {
            this.currencies = value.map((elem) => {
              return {
                name: elem,
                fn: () => {
                  this.currencySubject.next(elem)
                },
              }
            })

            if (this.currencies.length > 0) {
              if (
                !this.currencies.find((elem) => elem == this.activeCurrency)
              ) {
                this.setCurrency(this.currencies[0])
              } else {
                this.setCurrency(this.activeCurrency)
              }
            } else {
              this.currencySubject.next(null)
            }
          }), //end tap
        )
        .subscribe(),
    )

    this.subscriptions.push(
      this.currencySubject
        .pipe(
          combineLatest(this.xAxisTypeSubject, this.stackTypeSubject),
          debounceTime(20),
          tap(() => this.loading$.next(true)),
          switchMap((value: [string, XAxisType, StackType]) =>
            of(value).pipe(
              flatMap((value: [string, XAxisType, StackType]) => {
                if (value[0]) {
                  return this.dataService.getTransactionChartData(
                    this.contextSvc.activeDomain,
                    this.filter,
                    this.range,
                    value[0],
                    value[1],
                    value[2],
                  )
                } else {
                  return of(null)
                }
              }),
              catchError((err) => {
                this.logger.error(err)
                this.notify.error(err.message)
                return of(null)
              }),
            ),
          ), //end switchMap
          tap((res) => {
            this.txnData = res

            if (this.txnData) {
              /* List of totals */
              this.totalsTypes = Object.keys(this.txnData.totals)

              /* Build list of chart types */
              this.chartTypes = Object.keys(this.txnData.charts).map((elem) => {
                return {
                  name: elem,
                  fn: () => {
                    this.activeChartSubject.next(
                      this.txnData.charts[elem].chartConfig,
                    )
                  },
                }
              })

              if (this.chartTypes.length > 0) {
                /* Check currenct active currency still exists */
                if (
                  this.activeChartType &&
                  this.txnData.charts[this.activeChartType.name]
                ) {
                  this.setChartType(this.activeChartType)
                } else {
                  this.setChartType(this.chartTypes[0])
                }
              } else {
                this.activeChartSubject.next(null)
              }
            } else {
              this.chartTypes = []
              this.activeChartSubject.next(null)
            }

            this.loading$.next(false)
          }), //end tap
        )
        .subscribe(),
    )

    /* When active chart subject emits then build a chart object */
    /* Note : As Chart object can only be built when DOM element exists this takes a bit of trickery */
    /* instead of ngIf we need to use hidden to hide canvas else  Chart cannot be constructed */
    this.subscriptions.push(
      this.activeChartSubject
        .pipe(
          tap((config) => {
            /* First destroy the old chart.js object */
            if (this.chart) {
              this.chart.destroy()
            }

            /* If we have a new config, build a new chart.js object */
            if (config != null) {
              /* Build new chart */
              this.chart = new Chart('canvas', config)
            } else {
              this.chart = null
            }
          }), //end tap
        )
        .subscribe(),
    )

    /* Now setup the initial xaxis and stack types */
    this.setXAxisType(this.xAxisTypes[1])
    this.setStackType(this.stackTypes[0])
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  ngOnChanges(changes: SimpleChanges) {
    this.logger.log(changes)

    if (changes.filter) {
      this.filterSubject.next(changes.filter.currentValue)
    }
    if (changes.range) {
      this.rangeSubject.next(changes.range.currentValue)
    }
  }

  setChartType(val: ChartSelector) {
    this.activeChartType = val
    if (this.activeChartType.fn) {
      this.activeChartType.fn()
    }
  }

  setXAxisType(val: ChartSelector) {
    this.activeXAxisType = val
    if (this.activeXAxisType.fn) {
      this.activeXAxisType.fn()
    }
  }

  setStackType(val: ChartSelector) {
    this.activeStackType = val
    if (this.activeStackType.fn) {
      this.activeStackType.fn()
    }
  }

  setCurrency(val: ChartSelector) {
    this.activeCurrency = val
    if (this.activeCurrency.fn) {
      this.activeCurrency.fn()
    }
  }
}
